import { Box, Typography } from '@mui/material'
import { StartHome } from '../../../assets/svgs/StartHome'
import { BlueButton } from '../../customComponents/buttons/BlueButton'
import { ArrowRightAlt } from '@mui/icons-material'
import { AnnualPlanStatus } from '../../../models/AnnualPlanStatus'
import { useState } from 'react'
import { AnnualPlanningTeamModal } from './AnnualPlanningTeamModal'
import { StartAnnualPlan } from '../../../services/AnnualPlanService'

interface Props {
  annualPlanId: number
  annualPlanStatus: AnnualPlanStatus
  rcnId: number
  onStartPlan: () => void
}

export const AnnualPlanGettingStarted = (props: Props): JSX.Element => {
  const [showPlanTeamModal, setShowPlanTeamModal] = useState(false)

  const handleStartAnnualPlan = (): void => {
    setShowPlanTeamModal(true)
  }

  const onCloseModal = (): void => {
    setShowPlanTeamModal(false)
  }

  const onPlanTeamSubmit = async (): Promise<void> => {
    await StartAnnualPlan(props.annualPlanId, props.rcnId)
    setShowPlanTeamModal(false)
    props.onStartPlan()
  }

  return (
    <Box data-testid="annualPlanGettingStarted">
      <StartHome
        style={{
          position: 'relative',
          width: '100%',
          height: 'fit-content',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}
      />

      <Box sx={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant='h3' mb='8px'>Getting Started</Typography>
        <Typography variant='body1'>
          The Statewide Autism Resources and Training Project (START) partners with Regional Collaborative Networks (RCN) across Michigan to serve as the statewide infrastructure for implementation of evidence-based practices. The goal of this partnership is to ensure that all students are taught in inclusive settings with peers by school-based staff prepared to deliver evidence-based educational and behavioral supports. START provides the RCN with funding and tools to deliver locally-accessible information, training, resources, and targeted support tailored to each region’s specific needs.
        </Typography>
        <Typography variant='body1'>
          Preparing an Annual Plan gives RCN the opportunity to identify and address local needs in key priority areas set by START. This allows START and the RCN to collaborate to build training, coaching, and peer to peer support capacity to serve students and families across the state.
        </Typography>
        <Typography variant='body1'>
          Submitting the RCN Annual Plan in the START Planning and Data System (PDS) serves as the RCN’s application for contract in response to START’s request for Regional Collaborative Networks to provide services and supports that improve accessible learning for autistic students and students with different learning needs in each region of Michigan.
        </Typography>
        <Typography variant='body1'>
          RCN Annual Plan submission initiates the process of contracting with START for funding to carry out approved activities. After the contract is issued, RCN will use the Contract Implementation Plan (CIP) to provide specific information addressing each priority area and track progress toward meeting contract goals. RCN will share the impact of the year’s activities through data collection and progress reporting as part of the contract with START.
        </Typography>
        <Typography sx={{ mt: '.75em', fontWeight: 600 }} variant='h5'>Financial Information</Typography>
        <ul className='mt-0'>
          <li><Typography variant='body1'>View the <a style={{ color: 'currentcolor' }} target='_blank' href='https://docs.google.com/document/d/1Ojt5fxeKvvCot5mERZaGGqihSdYFdoMw422SDIQkyFM/edit?tab=t.0'>RCN Contract Spending Guidelines</a> for allowable and non-allowable expenses.</Typography></li>
        </ul>
      </Box>

      <Box sx={{
        m: '0 32px',
        p: '32px',
        border: '1px solid var(--blue-300)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}
      >
        <Box display='flex' flexDirection='column' gap='8px'>
          <Typography variant='h5' color='var(--blue-700)'>
            START's Compelling Why
          </Typography>
          <Typography variant='body1'>
            Ensuring all students are taught in inclusive settings with peers by staff prepared to deliver evidence-based educational and behavioral supports.
          </Typography>

        </Box>

        {props.annualPlanStatus === AnnualPlanStatus.NotStarted && (
          <BlueButton sx={{ width: 'fit-content' }} onClick={handleStartAnnualPlan} dataTestId='startPlanButton'>
            Start Annual Plan <ArrowRightAlt />
          </BlueButton>
        )}
      </Box>

      {showPlanTeamModal &&
        <AnnualPlanningTeamModal
          closeModal={onCloseModal}
          onSubmit={onPlanTeamSubmit}
          annualPlanId={props.annualPlanId}
        />
      }
    </Box>
  )
}
